import { Modal } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { unApi } from '../../shared/common/api/endpoints/reports/unApi';

interface Dependent {
    id: number;
    name: string;
}

type DeleteResult = 'deleted' | 'cancelled' | 'error';

/**
 * Удаляет кастомную метрику с проверкой зависимостей.
 * Если метрика используется в формулах других метрик — бэк возвращает
 * requires_confirmation со списком зависимых; тогда показываем предупреждение
 * и удаляем каскадно (метрику + зависимые) только по подтверждению.
 *
 * @param metricId id удаляемой метрики
 * @param pathname текущий путь (для заголовка Pathname)
 * @param options.onDeleted вызывается со списком id ВСЕХ удалённых метрик (сама + зависимые)
 * @returns 'deleted' | 'cancelled' | 'error'
 */
export const deleteCustomMetricWithConfirm = async (
    metricId: number | string,
    pathname: string,
    options: { onDeleted: (deletedIds: number[]) => void | Promise<void> }
): Promise<DeleteResult> => {
    const firstRes = await unApi.removeCustomMetric(metricId, pathname);
    const data = firstRes?.data || {};

    // Есть зависимые метрики — метрика НЕ удалена, спрашиваем подтверждение
    if (data.requires_confirmation) {
        const dependents: Dependent[] = data.dependents || [];
        return new Promise<DeleteResult>((resolve) => {
            Modal.confirm({
                title: 'Метрика используется в расчёте других',
                icon: <ExclamationCircleOutlined />,
                width: 520,
                content: (
                    <div>
                        <p>
                            Эта метрика участвует в формулах других метрик. Если удалить, вместе
                            с ней будут удалены и зависимые:
                        </p>
                        <ul style={{ marginBottom: 12 }}>
                            {dependents.map((dep) => (
                                <li key={dep.id}>
                                    <b>{dep.name || `#${dep.id}`}</b>
                                </li>
                            ))}
                        </ul>
                        <p>Удалить метрику вместе с зависимыми?</p>
                    </div>
                ),
                okText: 'Удалить всё',
                cancelText: 'Отмена',
                okButtonProps: { danger: true },
                onOk: async () => {
                    try {
                        const confirmedRes = await unApi.removeCustomMetric(metricId, pathname, true);
                        const deleted = confirmedRes?.data?.deleted_metrics || [Number(metricId)];
                        await options.onDeleted(deleted);
                        resolve('deleted');
                    } catch (e) {
                        resolve('error');
                    }
                },
                onCancel: () => resolve('cancelled'),
            });
        });
    }

    // Зависимых нет — метрика удалена сразу
    const deleted = data.deleted_metrics || [Number(metricId)];
    await options.onDeleted(deleted);
    return 'deleted';
};
